import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const Tooltip = ({ content, children, position = 'top', maxWidth = '300px' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top, left;

      switch (position) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + 8;
          break;
        default:
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      }

      // Adjust if tooltip goes outside viewport
      if (left < 10) left = 10;
      if (left + tooltipRect.width > viewportWidth - 10) {
        left = viewportWidth - tooltipRect.width - 10;
      }
      if (top < 10) top = triggerRect.bottom + 8;
      if (top + tooltipRect.height > viewportHeight - 10) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setTooltipPosition({ top, left });
    }
  }, [isVisible, position]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);
  const handleClick = () => setIsVisible(!isVisible);
  const handleFocus = () => setIsVisible(true);
  const handleBlur = () => setIsVisible(false);

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        className="inline-flex items-center cursor-help"
        tabIndex={0}
      >
        {children}
      </div>

      {isVisible && createPortal(
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 md:hidden"
            style={{ zIndex: 2147483645 }}
            onClick={() => setIsVisible(false)}
          />

          {/* Tooltip */}
          <div
            ref={tooltipRef}
            className="fixed px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg border border-gray-700"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              zIndex: 2147483646,
              maxWidth,
              whiteSpace: 'normal',
              wordWrap: 'break-word'
            }}
          >
            {content}

            {/* Arrow */}
            <div
              className={`absolute w-2 h-2 bg-gray-900 border-gray-700 transform rotate-45 ${
                position === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2 border-b border-r' :
                position === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2 border-t border-l' :
                position === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2 border-t border-r' :
                position === 'right' ? 'left-[-4px] top-1/2 -translate-y-1/2 border-b border-l' :
                'bottom-[-4px] left-1/2 -translate-x-1/2 border-b border-r'
              }`}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
